import React, { useCallback, useMemo, useContext } from "react";
import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { Arrow } from "../Arrow/Arrow";
import { RenderDates } from "../RenderDates/RenderDates";
import { PlanningGridContext, SelectionContext } from "../PlanningGrid";
import { MaterialAttachmentRow } from "./MaterialAttachmentRow";
import { MaterialVersionRow } from "./MaterialVersionRow";
import {
  getMostRecentPerKey,
  mapVersionAttachments,
  CHECKBOX_SX,
} from "./MaterialSection.utils";
import { createSelectableAddendumItem } from "../AddendumSection/addendumSection.utils";
import { AddendumSection } from "../AddendumSection/AddendumSection";
import { ReactComponent as AddendumIcon } from "../../../../assets/icons/addendum.svg";

export const MaterialSection = ({
  id,
  data,
  name,
  contentId,
  handleEdit,
  handleDelete,
  handleDeleteAddendum,
  handleDeleteVersion,
  attachmentActions,
  handleOpenReleaseTexts,
  handleEditFile,
}) => {
  const {
    headerGroups,
    grid,
    isSelectionMode: selectionMode,
    openIds,
    toggleOpenId,
  } = useContext(PlanningGridContext);
  const { selectedFileIds, selectedFiles, setSelectedFiles } =
    useContext(SelectionContext);
  const open = selectionMode || openIds.includes(id);

  const currentTypeId = data?.[0]?.typeId;
  const isEpisodeType = Number(data?.[0]?.type?.id) === 1211;

  const episodeNumber =
    Number(currentTypeId) === 1211 ? data?.[0]?.episodeNumber : null;

  const filteredData = useMemo(
    () => data?.filter((item) => item.typeId === currentTypeId),
    [data, currentTypeId]
  );

  const combinedDates = useMemo(
    () => ({
      planning: getMostRecentPerKey(filteredData, "planning"),
      realized: getMostRecentPerKey(filteredData, "realized"),
    }),
    [filteredData]
  );

  const allAttachments = useMemo(() => {
    const versionAttachments = data.flatMap(mapVersionAttachments);
    const addendums = (data || [])
      .flatMap((version) => version.addenda || [])
      .map((addendum) => createSelectableAddendumItem(addendum, false));
    return [...versionAttachments, ...addendums];
  }, [data]);

  const maxVersion = useMemo(
    () => Math.max(...(data || []).map((item) => Number(item.version) || 0)),
    [data]
  );

  const toggleMany = useCallback(
    (attachments) => {
      setSelectedFiles((prev) => {
        const prevIds = new Set(prev.map((f) => f.id));
        const allSelected = attachments.every((a) => prevIds.has(a.id));

        if (allSelected) {
          const removeIds = new Set(attachments.map((a) => a.id));
          return prev.filter((f) => !removeIds.has(f.id));
        }

        const newOnes = attachments.filter((a) => !prevIds.has(a.id));
        return [...prev, ...newOnes];
      });
    },
    [setSelectedFiles]
  );

  const toggleOne = useCallback(
    (attachment, parent) => {
      setSelectedFiles((prev) => {
        if (prev.some((f) => f.id === attachment.id)) {
          return prev.filter((f) => f.id !== attachment.id);
        }
        return [
          ...prev,
          {
            ...attachment,
            parentId: parent.id,
            type: "material",
            versionNumber: parent.version,
          },
        ];
      });
    },
    [setSelectedFiles]
  );

  const allGroupSelected = useMemo(
    () =>
      allAttachments.length > 0 &&
      allAttachments.every((a) => selectedFileIds.has(a.id)),
    [allAttachments, selectedFileIds]
  );

  const someGroupSelected = useMemo(
    () =>
      allAttachments.some((a) => selectedFileIds.has(a.id)) &&
      !allGroupSelected,
    [allAttachments, selectedFileIds, allGroupSelected]
  );

  const hasAddendumInMaterial = useMemo(
    () => (data || []).some((version) => !!version.addenda?.length),
    [data]
  );

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        display="grid"
        gridTemplateColumns={grid}
        sx={{
          bgcolor: open ? "#ffffff" : "#F6F6FE",
          borderBottom: open ? "1px solid #e0e2ff" : "1px solid transparent",
          minHeight: "56px",
          pl: "16px",
          pr: "16px",
        }}
      >
        {selectionMode && (
          <Box display="flex" justifyContent="center" alignItems="center">
            <Checkbox
              checked={allGroupSelected}
              indeterminate={someGroupSelected}
              onChange={() => toggleMany(allAttachments)}
              disabled={allAttachments.length === 0}
              size="small"
              sx={CHECKBOX_SX}
            />
          </Box>
        )}
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: "pointer" }}
          onClick={() => !selectionMode && toggleOpenId(id)}
        >
          {!selectionMode && (
            <IconButton size="small" disableRipple sx={{ p: 0 }}>
              <Arrow isOpen={open} margin="0 14px 0 16px" />
            </IconButton>
          )}
          <Typography
            fontWeight="bold"
            sx={{ fontFamily: "inherit", userSelect: "none" }}
          >
            {name} {episodeNumber}
          </Typography>
        </Box>

        <RenderDates
          columns={headerGroups}
          planning={combinedDates.planning}
          realized={combinedDates.realized}
        />

        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          flex="1"
          sx={{ pr: "8px" }}
        >
          <Tooltip
            title="Possui adendo associado"
            placement="top"
            slotProps={{
              tooltip: {
                sx: {
                  bgcolor: "#FFFFFF",
                  color: "#1F1F29",
                  border: "1px solid #E2E4FF",
                  boxShadow: "0px 4px 12px rgba(31, 31, 41, 0.12)",
                  fontFamily: '"Globotipo Text", sans-serif',
                  fontSize: "12px",
                  fontWeight: 500,
                  p: "4px 6px",
                },
              },
              arrow: {
                sx: {
                  color: "#FFFFFF",
                  "&:before": {
                    border: "1px solid #E2E4FF",
                  },
                },
              },
            }}
            arrow
          >
            <Box
              component={AddendumIcon}
              aria-label="Adendo"
              sx={{
                mr: "3px",
                visibility:
                  !open && hasAddendumInMaterial ? "visible" : "hidden",
              }}
            />
          </Tooltip>
        </Box>
      </Box>

      <Collapse in={open} timeout={0} unmountOnExit>
        <Box>
          {data?.map((versao, versaoIndex) => {
            const isLastVersion = Number(versao.version) === maxVersion;
            const hasAttachments = (versao.attachments || []).length > 0;
            const canDeleteWholeVersion = isLastVersion && !hasAttachments;

            const attachments = mapVersionAttachments(versao);

            return (
              <Box key={versao.id}>
                <Box
                  sx={{ bgcolor: "#ffffff", pl: "16px", pr: "16px", pb: "8px" }}
                >
                  {/* LINHA DA VERSÃO */}
                  <MaterialVersionRow
                    versao={versao}
                    attachments={attachments}
                    canDeleteWholeVersion={canDeleteWholeVersion}
                    handleEdit={handleEdit}
                    handleDeleteVersion={handleDeleteVersion}
                    toggleMany={toggleMany}
                  />

                  {/* ANEXOS */}
                  {versao.attachments?.map((anexo) => (
                    <MaterialAttachmentRow
                      key={anexo.fileId}
                      anexo={anexo}
                      versao={versao}
                      materialVersions={data}
                      contentId={contentId}
                      selectionMode={selectionMode}
                      selectedFileIds={selectedFileIds}
                      toggleOne={toggleOne}
                      attachmentActions={attachmentActions}
                      handleOpenReleaseTexts={handleOpenReleaseTexts}
                      handleEditFile={handleEditFile}
                      handleDelete={handleDelete}
                    />
                  ))}
                </Box>

                {/* ADENDOS DO EPISÓDIO */}
                {isEpisodeType &&
                  !!versao.addenda?.length &&
                  versaoIndex === data.length - 1 && (
                    <AddendumSection
                      id={`addendum-versao-${versao.id}`}
                      addenda={versao.addenda}
                      label={`Adendos Episódio ${versao.episodeNumber}`}
                      scope="episode"
                      attachmentActions={attachmentActions}
                      handleDeleteAddendum={handleDeleteAddendum}
                      handleEditAddendum={(addendum) =>
                        handleEditFile({
                          ...addendum.attachment,
                          addendumId: addendum.attachmentId,
                          isMaterial: true,
                          isAddendum: true,
                          materialId: versao.id,
                        })
                      }
                      handleOpenReleaseTexts={handleOpenReleaseTexts}
                      selectedFiles={selectedFiles}
                      setSelectedFiles={setSelectedFiles}
                    />
                  )}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};
